import { Action, ActionList, OverflowAction, useCan } from '@if/mui'
import { access } from '@src/access'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { Banknote, CalendarRange, FileX, History, Plus, Repeat, Timer } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ELoanCalendarAction, useLoanCalendarAction } from '../LoanCalendarActionProvider'

const LoanCalendarTablePrimaryActions = () => {
  const { t } = useTranslation()
  const { dispatchAction } = useLoanCalendarAction()
  const canPerformChargeOff = useCan(access.performChargeOff)
  const { isReadOnly } = useLoanLock()

  const actions = useMemo<Action[]>(
    () => [
      {
        icon: Plus,
        label: t('loan.payment.add'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddPaymentDialog),
      },
      {
        icon: Plus,
        label: t('loan.payment.addFee'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddFeeDialog),
      },
      {
        icon: History,
        label: t('loan.payment.reset'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenResetPaymentCalendarDialog),
      },
    ],
    [t, dispatchAction, isReadOnly],
  )

  const overflowActions = useMemo<OverflowAction[]>(
    () => [
      {
        icon: CalendarRange,
        label: t('loan.payment.moveCalendar'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenMoveCalendarDialog),
      },
      {
        icon: Timer,
        label: t('loan.payment.editPaymentFrequency.title'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenUpdatePaymentFrequencyDialog),
      },
      {
        icon: Banknote,
        label: t('loan.payment.editRegularPayment'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenChangeRegularPmtDialog),
      },
      {
        icon: Repeat,
        label: t('loan.payment.addRecurringPayments.title'),
        disabled: isReadOnly,
        onClick: () => dispatchAction(ELoanCalendarAction.OpenAddRecurringPaymentsDialog),
      },
      ...(canPerformChargeOff
        ? [
            {
              icon: FileX,
              label: t('loan.payment.chargeOff'),
              disabled: isReadOnly,
              onClick: () => dispatchAction(ELoanCalendarAction.OpenChargeOffDialog),
            } satisfies OverflowAction,
          ]
        : []),
    ],
    [t, dispatchAction, canPerformChargeOff, isReadOnly],
  )

  return (
    <ActionList
      variant="button"
      size="small"
      menuBelow="md"
      actions={actions}
      overflowActions={overflowActions}
      overflowLabel={t('loan.payment.otherGlobalActions')}
    />
  )
}

export default LoanCalendarTablePrimaryActions
